import { useState } from "react";
import { useTheme } from "@/lib/ground";
import { cn } from "@/lib/utils";
import { ContrastTeamBadge } from "./contrast-team-badge";
import { TeamChip } from "./team-chip";

/** Keep the crest separate from the kit badge beside the name. Without usable
 * artwork the badge moves into the crest's place, never appearing twice. */
export function ClubIdentity({
  name,
  logo,
  logoDark,
  short,
  color,
  large = false,
  layout = large ? "row" : "gallery",
  headingAs,
}: {
  name: string;
  logo?: string | undefined;
  logoDark?: string | undefined;
  short: string;
  color: string;
  large?: boolean;
  /** Compact workspaces keep the crest beside the identity instead of above it. */
  layout?: "gallery" | "row";
  headingAs?: "h1" | "h2" | "h3";
}) {
  const theme = useTheme();
  const [failed, setFailed] = useState<readonly string[]>([]);
  const variants = theme === "dark" ? [logoDark, logo] : [logo, logoDark];
  const source = variants.find((value) => value && !failed.includes(value));
  const Heading = headingAs ?? (large ? "h1" : "h2");
  return (
    <div
      className={cn("min-w-0", layout === "row" && "flex items-center gap-3", large && "sm:gap-6")}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex shrink-0 items-center justify-center",
          layout === "row" ? (large ? "size-16 sm:size-24" : "size-16") : "h-28 sm:h-36",
          layout === "row" && !source && "w-20",
        )}
        onErrorCapture={() => {
          if (source) setFailed((urls) => [...urls, source]);
        }}
      >
        {source ? (
          <ContrastTeamBadge
            logo={source}
            color={color}
            short={short}
            loading={layout === "row" ? "eager" : "lazy"}
            className={layout === "row" ? "size-full" : "size-20 sm:size-28"}
          />
        ) : (
          <TeamChip
            color={color}
            short={short}
            size="lg"
            className={cn(
              layout === "row" ? "px-3 py-2 text-xl sm:text-2xl" : "px-4 py-2 text-2xl sm:text-3xl",
            )}
          />
        )}
      </span>
      <div className={cn("flex min-w-0 items-center gap-2", large && "sm:gap-3")}>
        {source && (
          <span aria-hidden="true" className="inline-flex shrink-0">
            <TeamChip
              color={color}
              short={short}
              size="sm"
              className={large ? "sm:px-3 sm:py-1.5 sm:text-lg" : "sm:py-1 sm:text-sm"}
            />
          </span>
        )}
        <Heading
          className={cn(
            "nxt min-w-0 leading-tight text-pretty [overflow-wrap:anywhere]",
            large ? "text-2xl uppercase sm:text-4xl" : "text-base sm:text-2xl",
          )}
        >
          {name}
        </Heading>
      </div>
    </div>
  );
}
